import { StepName, StepProps } from "../../types";
import { useSignupStore } from "components/Signup/store/hooks";
import {
  getCurrentDraftForInsurance,
  selectHasAnimalLiabilityInsurance,
  useAllValidAnimals,
  useBillingInterval,
  useTotalCost,
} from "components/Signup/store/selectors/animals";
import {
  addLiabilityInsuranceOnAnimal,
  removeLiabilityInsuranceOnAnimal,
} from "components/Signup/store/slices/animals";
import { useState } from "react";
import { QuotePreviewBottomBar } from "components/Signup/components/QuotePreviewBottomBar";
import StickyBottom from "components/Signup/components/StickyBottom/StickyBottom";
import { BuyButton } from "components/Signup/components/BuyButton";
import { formatCurrency } from "utils/utils";
import Gutter from "ui-kit/Gutter";
import { useIsLoading } from "components/Signup/store/selectors/metadata";
import { Typography } from "ui-kit/Typography";
import { Trans, useWebTranslation } from "translations";
import { LiabilityAddonBox } from "components/Signup/components/LiabilityAddonBox";
import { trackSignupEvent } from "components/Signup/analytics";
import { updateCustomer } from "components/Signup/store/slices/customer";
import { useCustomer } from "components/Signup/store/selectors/customer";
import {
  ProductEnum,
  SpeciesEnum,
} from "../../../../../../__generated__/globalTypes";
import ShowOnMedia from "ui-kit/ShowOnMedia";
import MobileFullPackages from "components/ContentfulRenderer/ContentfulPackages/MobileFullPackages/MobileFullPackages";
import InsurancePackagesTable from "components/ContentfulRenderer/ContentfulPackages/InsurancePackagesTable/InsurancePackagesTable";
import { getPackageConfig } from "components/ContentfulRenderer/ContentfulPackages/getPackageConfig";
import { Button } from "ui-kit/Button";
import DateInput, { DateChange } from "components/DateInput/DateInput";
import { subYears } from "date-fns";
import Colors from "ui-kit/colors";
import SignupDocuments from "components/Signup/components/SignupDocuments/SignupDocuments";
import { SocialProof } from "components/Signup/components/SocialProof";
import { Icon, Modal } from "ui-kit/";
import { selectLiabilityInsuranceByAnimalId } from "components/Signup/store/selectors/insurance";
import { getAmountToPay } from "utils/insurance/getAmountToPay";
import { YEARLY_BILLING_INTERVAL } from "utils/insurance";

const StyledDateInput = ({
  value,
  onChange,
  onBlur,
  hasError,
}: {
  value: string | null;
  onChange: ({ date, validation }: DateChange) => void;
  onBlur: ({ date, validation }: DateChange) => void;
  hasError?: boolean;
}) => {
  const { t } = useWebTranslation();

  const minAge = subYears(new Date(), 120);
  const maxAge = subYears(new Date(), 18);

  return (
    <div
      style={{
        border: `1px solid ${Colors.grey200}`,
        borderRadius: 24,
        padding: 4,
      }}
    >
      <DateInput
        ariaInvalid={hasError}
        ariaDescribedBy={hasError ? "error" : undefined}
        ariaLabel={t("global.birthdate")}
        border={false}
        value={value ?? ""}
        onChange={onChange}
        onBlur={onBlur}
        min={minAge}
        max={maxAge}
        noValidate
        autoComplete="birthdate"
      />
    </div>
  );
};

const BirthdayModal = ({
  onBirtdayEntered,
}: {
  onBirtdayEntered: ({ date }: { date: string }) => void;
}) => {
  const { birthdate } = useCustomer();
  const [localDate, setLocalDate] = useState<string | null>(birthdate);

  const [dateValid, setDateValid] = useState(true);
  const [dateValidMin, setDateValidMin] = useState(true);
  const [dateValidMax, setDateValidMax] = useState(true);

  const { t } = useWebTranslation();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      <Typography variant="body3">
        {t("liability_addon.birthdate_question.description")}
      </Typography>
      <StyledDateInput
        value={birthdate}
        onChange={({ date, validation }) => {
          setDateValid(validation.isValid);
          setDateValidMax(validation.isValidMax);
          setDateValidMin(validation.isValidMin);

          if (
            validation.isValid &&
            validation.isValidMin &&
            validation.isValidMax
          ) {
            setLocalDate(date);
          }
        }}
        onBlur={({ validation, date }) => {
          setDateValid(validation.isValid);
          setDateValidMin(validation.isValidMin);
          setDateValidMax(validation.isValidMax);
          if (
            validation.isValid &&
            validation.isValidMin &&
            validation.isValidMax
          ) {
            setLocalDate(date);
          }
        }}
      />
      {!(dateValid && dateValidMin) && (
        <Typography color={"error"} testId="date-error">
          {t("date_input.error")}
        </Typography>
      )}
      {!dateValidMax && (
        <Typography color={"error"}>
          {t("validation.underage_person")}
        </Typography>
      )}
      <Button
        disabled={!localDate || !dateValid || !dateValidMin || !dateValidMax}
        onPress={() => {
          if (!localDate) {
            return;
          }

          onBirtdayEntered({ date: localDate });
        }}
        title={t("liability_addon.birthdate_question.submit")}
      />
    </div>
  );
};

const Addons = ({ next, isValid, transitioning }: StepProps) => {
  const { t } = useWebTranslation();

  const animals = useAllValidAnimals();

  const billingInterval = useBillingInterval();

  const [animalIdForAddon, setAnimalIdForAddon] = useState<string | null>(null);

  const { birthdate } = useCustomer();

  const [localDate, setLocalDate] = useState<string | null>(birthdate);

  const [dateValid, setDateValid] = useState(true);
  const [dateValidMin, setDateValidMin] = useState(true);
  const [dateValidMax, setDateValidMax] = useState(true);
  const isLoading = useIsLoading();

  const { dispatch, getState } = useSignupStore();

  const totalCost = useTotalCost();

  const packageConfig = getPackageConfig({
    product: ProductEnum.liability,
  });

  if (!packageConfig) {
    next();
    return null;
  }

  const defaultLiabilityPackage = {
    ...packageConfig,
    packages: packageConfig?.packages.filter((p) => p.deductible === 0),
    benefits: packageConfig?.benefits.filter(
      (b) => b.name !== "liabilityAdditionalOptions"
    ),
  };

  const eligibleAnimals = animals.filter(
    (animal) => animal.species === SpeciesEnum.dog
  );

  return (
    <>
      <div
        style={{
          maxWidth: 600,
          margin: "0 auto",
          padding: 16,
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        <Typography variant="h2" align="center">
          {t("liability_addon.title")}
        </Typography>
        <Gutter size={3} />

        <SocialProof />

        <Gutter size={3} />

        <LiabilityAddonBox
          title={t("liability_addon.box.title")}
          badge={t("liability_addon.box.discount")}
          summary={
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Typography variant="body3" color="grey500">
                {eligibleAnimals.length === 1
                  ? t("liability_addon.box.description", {
                      name: eligibleAnimals[0].name ?? "",
                    })
                  : t("liability_addon.box.description_multipet")}
              </Typography>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Icon name="checkmark" size="S" color={Colors.green200} />
                <Typography variant="body3" color="grey500">
                  {t("liability_addon.box.usp1")}
                </Typography>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Icon name="checkmark" size="S" color={Colors.green200} />
                <Typography variant="body3" color="grey500">
                  {t("liability_addon.box.usp2")}
                </Typography>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Icon name="checkmark" size="S" color={Colors.green200} />
                <Typography variant="body3" color="grey500">
                  {t("liability_addon.box.usp3")}
                </Typography>
              </div>
            </div>
          }
          options={eligibleAnimals.map((animal) => {
            const liabilityInsurance = selectLiabilityInsuranceByAnimalId(
              getState(),
              animal.id
            );
            const liabilityDraft = liabilityInsurance
              ? getCurrentDraftForInsurance(liabilityInsurance)
              : null;
            const liabilityPrice = liabilityDraft
              ? getAmountToPay({ ...liabilityDraft, billingInterval })
              : null;

            return {
              title: liabilityPrice ? (
                <>
                  {eligibleAnimals.length > 1 ? animal.name + " - " : ""}
                  <Trans
                    i18nKey="liability_addon.box.price"
                    components={[<s key="0" />]}
                    values={{
                      oldPrice: undefined,
                      newPrice: `${formatCurrency(liabilityPrice.old)}/${
                        billingInterval === YEARLY_BILLING_INTERVAL
                          ? t("global.date.year")
                          : t("global.date.month")
                      }`,
                    }}
                  />
                </>
              ) : eligibleAnimals.length === 1 ? (
                t("liability_addon.box.switch_description")
              ) : (
                animal.name ?? ""
              ),
              onChange: () => {
                if (!birthdate) {
                  setAnimalIdForAddon(animal.id);
                } else {
                  const hasLiabilityInsurance =
                    selectHasAnimalLiabilityInsurance(getState(), animal.id);
                  if (hasLiabilityInsurance) {
                    dispatch(
                      removeLiabilityInsuranceOnAnimal({ animalId: animal.id })
                    );
                  } else {
                    dispatch(
                      addLiabilityInsuranceOnAnimal({ animalId: animal.id })
                    );
                  }
                  trackSignupEvent(
                    "LIABILITY_ADDON_SELECTED" as StepName,
                    0,
                    getState()
                  );
                }
              },
              isActive: selectHasAnimalLiabilityInsurance(
                getState(),
                animal.id
              ),
            };
          })}
          readmore={
            <>
              <ShowOnMedia desktop={false} tablet={true} mobile={true}>
                <MobileFullPackages
                  config={defaultLiabilityPackage}
                  defaultSelectedIndex={0}
                  expanded
                />
              </ShowOnMedia>
              <ShowOnMedia desktop={true} tablet={false} mobile={false}>
                <InsurancePackagesTable config={defaultLiabilityPackage} />
              </ShowOnMedia>
            </>
          }
        />
        <Typography variant="body3" color="grey500" align="center">
          {t("liability_addon.disclaimer")}
        </Typography>

        {birthdate && (
          <>
            <Gutter size={3} />
            <Typography variant="body3">
              <strong>{t("liability_addon.birthdate.label")}</strong>
            </Typography>
            <Gutter size={1} />
            <StyledDateInput
              value={birthdate}
              onChange={({ date, validation }) => {
                setDateValid(validation.isValid);
                setDateValidMin(validation.isValidMin);
                setDateValidMax(validation.isValidMax);
                if (date && dateValid && dateValidMin && dateValidMax) {
                  dispatch(updateCustomer({ birthdate: date }));
                }
              }}
              onBlur={({ date, validation }) => {
                setDateValid(validation.isValid);
                setDateValidMin(validation.isValidMin);
                setDateValidMax(validation.isValidMax);
                if (date && dateValid && dateValidMin && dateValidMax) {
                  dispatch(updateCustomer({ birthdate: date }));
                }
              }}
              hasError={!(dateValid && dateValidMin && dateValidMax)}
            />
            {!(dateValid && dateValidMin) && (
              <Typography color={"error"} testId="date-error" id="error">
                {t("date_input.error")}
              </Typography>
            )}
            {!dateValidMax && (
              <Typography color={"error"} id="error">
                {t("validation.underage_person")}
              </Typography>
            )}
          </>
        )}

        <Modal
          isOpen={animalIdForAddon !== null}
          onClose={() => setAnimalIdForAddon(null)}
          title={t("liability_addon.birthdate_question.title")}
          width="small"
        >
          <BirthdayModal
            onBirtdayEntered={({ date }) => {
              dispatch(updateCustomer({ birthdate: date }));

              if (animalIdForAddon) {
                dispatch(
                  addLiabilityInsuranceOnAnimal({
                    animalId: animalIdForAddon,
                  })
                );
                trackSignupEvent(
                  "LIABILITY_ADDON_SELECTED" as StepName,
                  0,
                  getState()
                );

                setAnimalIdForAddon(null);
              }
            }}
          />
        </Modal>
      </div>

      {animals.length > 1 ? (
        <>
          <QuotePreviewBottomBar next={next} />
        </>
      ) : (
        <>
          <StickyBottom
            button={
              <BuyButton
                oldAmount={
                  totalCost.old !== totalCost.new
                    ? formatCurrency(totalCost.old)
                    : null
                }
                newAmount={formatCurrency(totalCost.new)}
                onPress={next}
                loading={isLoading || transitioning}
                disabled={
                  !isValid || !dateValid || !dateValidMin || !dateValidMax
                }
              />
            }
            transparent
          />
          <Gutter size={4} />
        </>
      )}
      <SignupDocuments
        documents={[
          {
            title: t("quote_preview.germany.ipid.liability"),
            url: "https://docs.lassie.co/ger/de/ipid/latest/Produktubersicht-Hundehalterhaftpflichtversicherung.pdf",
          },
          {
            title: t("quote_preview.germany.tac.liability"),
            url: "https://docs.lassie.co/ger/de/terms-and-conditions/latest/Allgemeine-Versicherungsbedingungen-Hundehalterhaftpflichtversicherung.pdf",
          },
        ]}
      />
    </>
  );
};

export default Addons;
